import { useEffect, useState } from "react";
import {
  WhiteLayerPreviewMode,
  WhiteLayerSettings,
} from "../types/whiteLayer";
import { buildPreviewMask } from "../utils/white-layer/buildPreviewMask";
import { buildPreviewOverlay } from "../utils/white-layer/buildPreviewOverlay";

type UseWhiteLayerPreviewParams = {
  imageUrl: string | null;
  settings: WhiteLayerSettings;
  mode: WhiteLayerPreviewMode;
};

type UseWhiteLayerPreviewResult = {
  previewUrl: string | null;
  loading: boolean;
};

export function useWhiteLayerPreview({
  imageUrl,
  settings,
  mode,
}: UseWhiteLayerPreviewParams): UseWhiteLayerPreviewResult {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!imageUrl) {
      setPreviewUrl(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const buildPreview = async () => {
      try {
        setLoading(true);

        const img = new Image();
        img.crossOrigin = "anonymous";

        const imageLoaded = new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("Failed to load preview image."));
        });

        img.src = imageUrl;
        await imageLoaded;

        if (cancelled) return;

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Could not create preview canvas context.");
        }

        ctx.drawImage(img, 0, 0);

        const originalData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const maskData = buildPreviewMask(originalData, settings);
        const previewData = buildPreviewOverlay(originalData, maskData, mode);

        ctx.putImageData(previewData, 0, 0);

        const nextPreviewUrl = canvas.toDataURL("image/png");

        if (!cancelled) {
          setPreviewUrl(nextPreviewUrl);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("useWhiteLayerPreview error:", error);
          setPreviewUrl(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    buildPreview();

    return () => {
      cancelled = true;
    };
  }, [imageUrl, settings, mode]);

  return {
    previewUrl,
    loading,
  };
}